import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, LOGOS } from "@/lib/seed-content";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-slate-200"
          : "bg-white/80 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="container-seed flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-3 group">
          <img
            src={LOGOS.linear}
            alt="SEED — Solar Energy Efficiency Developments"
            className="h-9 sm:h-10 w-auto"
          />
          <span className="hidden sm:inline-block text-[10px] tracking-[0.18em] uppercase text-slate-500 leading-tight border-l border-slate-200 pl-3">
            Solar Energy<br />Efficiency
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace(/\//g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium link-underline transition-colors ${
                  isActive ? "text-emerald-600" : "text-slate-700 hover:text-slate-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-book-assessment-btn"
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-2.5 px-5 rounded-lg transition-colors duration-200 text-sm"
          >
            Book Assessment
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-slate-900 hover:bg-slate-100"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-nav">
          <div className="container-seed py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.to.replace(/\//g, "-")}`}
                className={({ isActive }) =>
                  `block px-3 py-3 rounded-md text-sm font-medium ${
                    isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-800 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-book-assessment-btn"
              className="mt-2 inline-flex items-center justify-center bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 px-5 rounded-lg"
            >
              Book Assessment
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
